/**
 * Share grid builder. Pure unicode + plain text. Pastes identically into
 * Slack, Teams, iMessage, LinkedIn, X, Discord.
 *
 * Format (PRD §8 + brand DNA share_grid contract):
 *   Circle Back #042
 *   🟩🟩🟨🟩
 *   🟩🟥🟥🟩
 *   🟩🟩🟩🟩
 *   🟩🟩🟩🟩
 *   00:47 · 3/4 first try · streak 🔥7
 *   Beat my time → circlebackaustralia.com         ← if 4/4 first try
 *   Can you go 4/4? → circlebackaustralia.com      ← if not
 *
 * The trailing line is the office-competition challenge — adaptive based on
 * the player's score so the share itself is the prompt for the next player.
 *
 * Each row's length = number of attempts on that phrase.
 *  🟩 = correct first try
 *  🟨 = correct after retries (final cell only)
 *  🟥 = wrong attempt
 */

import type { GameState } from '@/types/game';
import { firstTryCount, elapsedSeconds } from './engine';

const SQ_GREEN = '\u{1F7E9}';
const SQ_YELLOW = '\u{1F7E8}';
const SQ_RED = '\u{1F7E5}';

const SITE_URL = 'circlebackaustralia.com';
const SITE_URL_FULL = 'https://circlebackaustralia.com';

export interface ShareInput {
  state: GameState;
  numericId: string; // e.g. "042"
  streak: number;
}

export function buildShareText({ state, numericId, streak }: ShareInput): string {
  const lines: string[] = [];
  lines.push(`Circle Back #${numericId}`);
  for (let j = 0; j < 4; j++) {
    const history = state.attemptHistory[j] ?? [];
    const cells = history.map((event) => {
      if (event === 'wrong') return SQ_RED;
      if (event === 'correct-after') return SQ_YELLOW;
      return SQ_GREEN;
    });
    lines.push(cells.join(''));
  }
  const seconds = elapsedSeconds(state);
  const ftCount = firstTryCount(state);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  let stats = `${mm}:${ss} · ${ftCount}/4 first try`;
  if (streak > 0) stats += ` · streak \u{1F525}${streak}`;
  lines.push(stats);
  lines.push(buildChallengeLine(ftCount));
  return lines.join('\n');
}

/**
 * Adaptive challenge line. 4/4 first try → "Beat my time". Anything less →
 * "Can you go 4/4?". Either way, the line invites the next player.
 */
export function buildChallengeLine(firstTry: number): string {
  if (firstTry === 4) return `Beat my time → ${SITE_URL}`;
  return `Can you go 4/4? → ${SITE_URL}`;
}

export interface ShareWithRankInput extends ShareInput {
  /** 0..100 — server-issued percentile from /api/score-submit. */
  percentile: number;
  /** Gary's declared par-time, default 19s. */
  pacePar: number;
}

/**
 * Build the share text with a single rank line inserted BEFORE the URL/challenge
 * line. Output remains plain text — pastes identically into Slack, Teams,
 * iMessage, LinkedIn, X, Discord.
 *
 * Format (PRD-LEADERBOARD §11):
 *   Circle Back #042
 *   🟩🟩🟩🟩
 *   ...
 *   00:47 · 4/4 first try · streak 🔥7
 *   Beat 71% of Australia today · Beat Gary's 19s?      ← NEW rank line
 *   Beat my time → circlebackaustralia.com
 *
 * The original buildShareText is NOT modified — this is an additive helper.
 * Frontend chooses which to use based on whether /api/score-submit returned
 * accepted=true with samples ≥ 10.
 */
export function buildShareTextWithRank(input: ShareWithRankInput): string {
  const baseText = buildShareText(input);
  const lines = baseText.split('\n');
  const rankLine = `Beat ${input.percentile}% of Australia today · Beat Gary's ${input.pacePar}s?`;
  // Insert before the last (URL/challenge) line.
  return [...lines.slice(0, -1), rankLine, lines.slice(-1)[0]].join('\n');
}

export interface EmailShare {
  subject: string;
  body: string;
  mailto: string;
}

/**
 * Build a mailto: link for emailing the result to a colleague.
 *
 * Pre-fills subject + body with a competition framing. No email-capture, no
 * server send — opens the user's native mail client. Brand-safe by design:
 * if the user's mail client doesn't open, nothing is sent and nothing is
 * captured.
 */
export function buildEmailShare(shareText: string, firstTry: number): EmailShare {
  const subject =
    firstTry === 4
      ? "Circle Back — beat my time"
      : "Circle Back — can you go 4/4?";
  const body = [
    "Today's Circle Back:",
    '',
    shareText,
    '',
    `New puzzle every weekday at 9am Melbourne. → ${SITE_URL_FULL}`,
  ].join('\n');
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return { subject, body, mailto };
}

/**
 * Copy a string to the clipboard. Returns true on success.
 * Tries the modern API first, falls back to execCommand for Safari < 13.4.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to fallback.
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Use the Web Share API if available, else copy. */
export async function shareOrCopy(
  text: string
): Promise<{ method: 'web-share' | 'clipboard'; ok: boolean }> {
  // Some browsers expose navigator.share but reject text-only payloads on desktop.
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (nav.share && typeof nav.share === 'function') {
    try {
      await nav.share({ text });
      return { method: 'web-share', ok: true };
    } catch {
      // User cancelled or unsupported.
    }
  }
  const ok = await copyToClipboard(text);
  return { method: 'clipboard', ok };
}
